import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import type { DetailSection } from '@/shared/app-detail-layout/types';
import { DeniedState, EmptyState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import type { AsyncResource } from '@/shared/hooks/use-async-resource';
import { getWorkspaceScope } from '@/app/workspace/store';
import { isForbidden } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import { EffectiveSettingsPanel } from './parts/effective-settings';
import { TenantOwnSettings } from './parts/tenant-own-settings';
import { TenantProviderForm } from './parts/tenant-provider-form';
import { UsageOverview } from './parts/usage-overview';
import type { AiUsageSummary, EffectiveAiSettings } from '@/api/schemas/ai';

/**
 * The tenant workspace's AI settings.
 *
 * ---------------------------------------------------------------------------
 * AI IS AN AID, NEVER A REQUIREMENT
 * ---------------------------------------------------------------------------
 * This screen reports what AI is doing for this workspace and lets the tenant
 * change it. Every state that is not `ready` says plainly that the manual
 * forms still work — a tenant whose AI is off, unconfigured, unavailable or
 * over its limit has lost nothing they cannot do by hand.
 */
export type TenantAiPageProps = {
  /** The tenant in the URL. The API resolves the tenant from the session. */
  tenantId: string;
};

export function TenantAiPage({ tenantId }: TenantAiPageProps): ReactNode {
  const settingsLoad = useCallback(() => aiApi.getEffectiveSettings(), []);
  const usageLoad = useCallback(() => aiApi.getUsage(), []);
  const settings = useAsyncResource(settingsLoad);
  const usage = useAsyncResource(usageLoad);

  const surface = tenantAiSurface({
    isLoading: settings.isLoading,
    error: settings.error,
    onRetry: settings.reload,
  });
  if (surface !== null) return surface;

  const effective = settings.data;
  if (effective === null) return null;

  const scopeName = getWorkspaceScope()?.tenant.slug;

  return (
    <AppDetailLayout
      headerVariant="accent"
      title="AI settings"
      subtitle={
        scopeName === undefined
          ? 'How AI works for this workspace.'
          : `${scopeName} — how AI works for this workspace.`
      }
      breadcrumbs={[
        { label: 'Workspace', href: `/tenant/${tenantId}/workspace` },
        { label: 'AI' },
      ]}
      tabs={buildTabs(effective)}
      sections={buildSections({ effective, usage, onChanged: settings.reload })}
      tabsInHeader
    />
  );
}

type TenantAiSurfaceParams = {
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
};

/** The surface that replaces the page body while there is nothing to show. */
function tenantAiSurface(params: TenantAiSurfaceParams): ReactNode | null {
  if (params.isLoading) {
    return <AppDetailLayout title="AI settings" sections={[]} isLoading />;
  }
  if (isForbidden(params.error)) {
    return (
      <AppDetailLayout
        title="AI settings"
        sections={[]}
        toolbar={
          <DeniedState
            title="Not permitted for your role"
            message="You cannot read this workspace's AI settings."
          />
        }
      />
    );
  }
  if (params.error) {
    return (
      <AppDetailLayout
        title="AI settings"
        sections={[]}
        toolbar={
          <ErrorState
            title="Could not load your AI settings"
            message={params.error.message}
            onRetry={params.onRetry}
          />
        }
      />
    );
  }
  return null;
}

function buildTabs(effective: EffectiveAiSettings): ReadonlyArray<{ id: string; label: string }> {
  const mayConfigureProvider = effective.tenantSettings?.allowTenantOwnedProvider === true;
  return [
    { id: 'settings', label: 'Settings' },
    ...(mayConfigureProvider ? [{ id: 'provider', label: 'Your provider' }] : []),
    { id: 'usage', label: 'Usage' },
  ];
}

type TenantAiSectionsParams = {
  effective: EffectiveAiSettings;
  usage: AsyncResource<AiUsageSummary>;
  onChanged: () => void;
};

function buildSections(params: TenantAiSectionsParams): ReadonlyArray<DetailSection> {
  const tenantSettings = params.effective.tenantSettings;
  const mayConfigureProvider = tenantSettings?.allowTenantOwnedProvider === true;

  return [
    {
      id: 'settings',
      label: 'Settings',
      content: (
        <div className="space-y-6">
          <EffectiveSettingsPanel settings={params.effective} />
          {tenantSettings === null ? null : (
            <TenantOwnSettings settings={tenantSettings} onSaved={params.onChanged} />
          )}
        </div>
      ),
    },
    {
      id: 'provider',
      label: 'Your provider',
      content: mayConfigureProvider ? (
        <TenantProviderForm
          tenantProviderId={tenantSettings?.tenantProviderId ?? null}
          onChanged={params.onChanged}
        />
      ) : (
        <EmptyState
          title="Your workspace uses the platform's provider"
          description="Your workspace is not permitted to configure a provider of its own. Everything still works — the platform's provider is used instead."
        />
      ),
    },
    {
      id: 'usage',
      label: 'Usage',
      content: usagePanel(params.usage),
    },
  ];
}

function usagePanel(usage: AsyncResource<AiUsageSummary>): ReactNode {
  if (usage.isLoading) {
    return <LoadingSkeleton shape="list" rows={3} label="Loading usage" />;
  }
  if (isForbidden(usage.error)) {
    return (
      <DeniedState
        title="Not permitted for your role"
        message="You cannot read this workspace's AI usage."
      />
    );
  }
  if (usage.error) {
    return (
      <ErrorState
        title="Could not load your usage"
        message={usage.error.message}
        onRetry={usage.reload}
      />
    );
  }
  if (usage.data === null) {
    return <EmptyState title="No usage yet" description="Nothing has been used in this period." />;
  }
  return <UsageOverview usage={usage.data} />;
}
